import React, { ReactNode, useContext, useState } from "react";
import { DesignerProviderValue, PhoenixFormsDesignerContext } from ".";
import { Question, Section } from "interfaces/Phoenix.FormsDesigner.Models";
import PNXLabel from "components/PhoenixFormsV4/Controls/PNXLabel";
import { classes } from "components/Utility";
import IconButton from "components/Shared/IconButton";
import { MSelect } from "components/Shared";
import { stopPropagationPreventDefault, stopPropagationPreventDefaultNoFn } from "tools/clickEvents";
import RadioGroup from "@mui/material/RadioGroup";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Checkbox from "@mui/material/Checkbox";
import { Button } from "components/Shared";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { timeISOToPicker } from "components/PhoenixFormsV4/Controls/PhoenixInput";
import ReorderableGrid, { ReorderableItem } from "components/Shared/ReorderableGrid";
import CMSHelpIcon from "components/CMS/CMSHelpIcon";
import { PNXTag } from "components/PhoenixFormsV4/Controls/PhoenixPhotoTagGroup";

interface DesignerQuestionProps {
  className?: string;
  question: Question;
  parentSection?: Section;
  parentQuestion?: Question;
};

export const reorderQuestionHandle = "reorder-question-handle";
export const reorderNestedQuestionHandle = "reorder-nested-question-handle";

const DesignerFormControl = ({ className, question, parentSection, parentQuestion }: DesignerQuestionProps) => {
  const context = useContext<DesignerProviderValue>(PhoenixFormsDesignerContext);
  const [isGroupRepeatCollapsed, setIsGroupRepeatCollapsed] = useState<boolean>(false);
  
  let controlPreview: ReactNode;
  let isGroupOrRepeat = false;
  let isGroupControl = false;
  let secondaryControlPreview: ReactNode = "";

  let labelValEnumeration = question.control.dataField?.enumeration?.items?.map(i => ({ label: <PNXLabel>{i.name}</PNXLabel> as any as string, value: i.value }));
  if (!labelValEnumeration?.length) labelValEnumeration = [{ label: "Sample Item", value: "" }];

  switch (question.control.type) {
    case "DynamicItemsetSelectOne":
    case "AdvancedDynamicItemsetSelectOne":
    case "SelectOne":
      switch (question.control.appearance) {
        case "compact":
          controlPreview = (
            <MSelect
              placeholder="Select..."
              isClearable
              options={labelValEnumeration}
            />
          );
          break;
        case "phoenix:combo":
          controlPreview = (
            <MSelect
              placeholder="Search or Enter Value..."
              isClearable
              creatable
              options={labelValEnumeration}
            />
          );
          break;
        case "minimal":
          controlPreview = (
            <MSelect
              placeholder="Search or Enter Value..."
              isClearable
              options={labelValEnumeration}
            />
          );
          break;
        case "full":
          controlPreview = (
            <RadioGroup>
              {labelValEnumeration.map((item, idx) => (
                <FormControlLabel key={idx} value={item.value} control={<Radio />} label={item.label} />
              ))}
            </RadioGroup>
          );
      }
      break;
    case "SelectMany":
      controlPreview = (
        <FormGroup>
          {labelValEnumeration.map((item, idx) => (
            <FormControlLabel key={idx} control={<Checkbox />} label={item.label} />
          ))}
        </FormGroup>
      );
      break;
    case "SelectGroup":
      controlPreview = (
        <FormGroup>
          {labelValEnumeration.map((item, idx) => (
            <FormControlLabel key={idx} control={<Checkbox />} label={item.label} />
          ))}
        </FormGroup>
      );
      break;
    case "TextBox":
      let sampleVal = {
        "Integer": "42",
        "Decimal": "1.23",
        "String": "Sample Text",
        "Date": "8/21/2025",
        "Time": "3:35 PM",
      }[question.control.dataField?.type ?? "String"];

      if (question.control.dataField?.type === "Date") {
        controlPreview = 
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={sampleVal ? new Date(sampleVal) : null}
              disabled
              slotProps={{ textField: { variant: "standard" } }}
            />
          </LocalizationProvider>;
      } else if (question.control.dataField?.type === "Time") {
        controlPreview = 
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              value={timeISOToPicker(sampleVal)}
              disabled
              slotProps={{ textField: { variant: "standard" } }}
            />
          </LocalizationProvider>;
      } else {
        controlPreview = (
          <TextField
            variant="standard"
            disabled
            value={sampleVal}
            placeholder="Enter Number..."
          />
        );
      }
      break;
    case "Label":
      controlPreview = null;
      break;
    case "Group":
    case "BoundGroup":
    case "Repeat":
      isGroupOrRepeat = true;

      if (question.control.type === "Group" || question.control.type === "BoundGroup") isGroupControl = true;

      let controls = question.control.controls.reduce((acc, c, i) => {
        if (c.type === "QuestionReference") {

          let innerQuestion = context.getQuestionById(c.questionId);
          let nextQuestion = context.getQuestionById(question.control.controls?.[i + 1]?.questionId);

          let isStartOfQuestionMerge = !innerQuestion.control.mergeWithAbove && nextQuestion?.control.mergeWithAbove;
          let isEndOfQuestionMerge = innerQuestion.control.mergeWithAbove && !nextQuestion?.control.mergeWithAbove;

          acc.push({
            id: c.questionId,
            node: <DesignerFormControl 
                    key={c.questionId}
                    question={innerQuestion}
                    parentQuestion={question}
                    className={classes((innerQuestion.control.mergeWithAbove || isStartOfQuestionMerge) && "merged-question", isStartOfQuestionMerge && "start-merged-question", isEndOfQuestionMerge && "end-merged-question")}
                  />,
          });
        }

        return acc;
      }, [] as ReorderableItem[]);

      if (question.control.type === "Repeat") {
        if (question.control.appearance === "compact") secondaryControlPreview = "Collapsible Repeat";
        else if (question.control.appearance === "static") secondaryControlPreview = "Readonly Repeat";
        else secondaryControlPreview = "Repeat";
      }

      if (isGroupControl) secondaryControlPreview = "Group";

      controlPreview = question.control.controls.length === 0 ? 
        <Button
          primary
          onClick={() => context.editFns.addNewQuestion({ repeatOrGroup: question })}
          className="add-question-to-group"
        >
          Add New Question
        </Button>
        :
        <ReorderableGrid
          className="section-controls repeat"
          itemClassName={isGroupControl && "group-reorder"}
          items={controls}
          handleSelector={`.${reorderQuestionHandle}`}
          onReorder={ids => context.editFns.reorderQuestions({ repeatOrGroup: question }, ids)}
        />;
      break;
    case "Photo":
      controlPreview = <IconButton icon="photo_camera" />;
      break;
    case "Calculate":
      controlPreview = <IconButton icon="calculate" />;
      break;
    case "Ruleset":
    case "Recommendation":
      controlPreview = <IconButton icon="square_foot" />;
      break;
    case "File":
      controlPreview = <IconButton icon="attach_file" />;
      break;
    case "Document":
      controlPreview = <><IconButton icon="photo_camera" /><IconButton icon="attach_file" /></>;
      break;
  }

  const divClassName =
    classes(
      "designer-question formcontrol",
      className,
      context?.selectedQuestion?.id === question.id && "selected-question", // we use this control to render form controls in the dialog, we dont have access to the context there
      isGroupControl && "group-control",
      question.control.type === "Repeat" && "repeat-control"
    );

  const controlWithoutPreview: ReactNode[] = [
    isGroupOrRepeat && <IconButton icon="keyboard_arrow_down" className={classes("md-collapser", !isGroupRepeatCollapsed && "md-collapser--flipped")} onClick={stopPropagationPreventDefault(() => setIsGroupRepeatCollapsed(!isGroupRepeatCollapsed))}/>,
    <IconButton icon="reorder" className={reorderQuestionHandle} onClick={stopPropagationPreventDefaultNoFn} />,
    <IconButton icon="add" onClick={stopPropagationPreventDefault(() => context.editFns.addNewQuestion({ modifierQuestionId: question.id, section: parentSection, repeatOrGroup: parentQuestion }))} />,
    !isGroupOrRepeat && <IconButton icon="content_copy" onClick={stopPropagationPreventDefault(() => context.clipboard.copy(question.id))} />,
    <IconButton icon="delete" onClick={stopPropagationPreventDefault(() => context.editFns.deleteQuestion(question.id))} />,
    <div className="designer-control-label"><PNXLabel>{question.control.label}</PNXLabel>{question.control.contentCode && <CMSHelpIcon topicId={context.cmsList?.find(cms => cms.uniqueKey === question.control.contentCode)?.topicId} />}</div>,
  ].filter(Boolean);

  return (
    <div className={divClassName} onClick={stopPropagationPreventDefault(() => context.setSelectedQuestion(question))}>
      {isGroupOrRepeat ? 
      <> {/* group / repeats */}
        <div className="control-title">{controlWithoutPreview}{secondaryControlPreview}{isGroupRepeatCollapsed}</div>
        {!isGroupRepeatCollapsed && <div className="control-preview">{controlPreview}</div>}
      </>
      :
      <> {/* single controls */}
        {controlWithoutPreview}
        {<div className="control-preview">{controlPreview}</div>}
        {question.control.hint && <PNXTag tag={question.control.hint} />}
      </>}
    </div>
  );
};

export default DesignerFormControl;
